"use client";

import React from "react";
import { Box, Typography, Stack } from "@mui/material";
import { ColletteButton } from "@collette-travel/collette-common-controls";
import { useTranslations } from "next-intl";
import { TourInclusion, OfferDetails } from "@/types/dto/pdp/pdp-dates-inclusions-pricing";
import InclusionItem from "./inclusion-item";

interface SpecialEventData {
  title: string;
  description: string;
}

interface InclusionsSectionProps {
  inclusions: TourInclusion[];
  offerDetails?: OfferDetails;
  specialEventData?: SpecialEventData;
  showSpecialEvent?: boolean;
  onRequestQuote?: () => void;
  onViewOfferDetails?: () => void;
  handleOpenOnTourAir?: () => void;
  hasOnTourAir: boolean;
  hasReverseItinerary: boolean;
}

const InclusionsSection = ({
  inclusions,
  offerDetails,
  specialEventData,
  showSpecialEvent = false,
  onRequestQuote,
  onViewOfferDetails,
  handleOpenOnTourAir,
  hasOnTourAir = false,
  hasReverseItinerary = false 
}: InclusionsSectionProps) => {
  const t = useTranslations("PDP");

  const cleanedInclusions = hasOnTourAir
    ? inclusions
    : inclusions.filter((inclusion) => inclusion.id !== "on-tour-air");

  return (
    <Stack
      spacing={2}
      sx={{
        width: { xs: "100%", lg: "50.5rem" },
        gap: "2rem"
      }}
      data-testid="inclusions-section"
      data-track="visibility"
      data-module-name="Inclusions Section"
      data-parent-container-type="pdp"
      data-ranking-algorithm="manual"
      data-module-ranking-position="9"
      component="section"
    >
      {/* Section Header */}
      <Typography
        variant="h2"
        sx={{
          fontSize: { xs: "2.4rem", md: "2.8rem" },
          fontWeight: 600,
          color: "text.primary",
          lineHeight: { xs: 1.3, md: "normal" }
        }}
        data-testid="offers-events-inclusions-section"
      >
        {t("Inclusions & Offers")}
      </Typography>

      {/* Cards Container - side by side on tablet only, stacked on mobile and desktop */}
      <Box
        sx={{
          display: "flex",

          flexDirection: { xs: "column", legacyTablet: "row", legacyDesktop: "column" },
          gap: "2rem"
        }}
      >
        {/* Have Questions Card */}
        <Box
          data-testid="have-questions-card"
          sx={{
            flex: { legacyTablet: 1, legacyDesktop: "none" },
            boxShadow: 6,

            backgroundColor: "background.default",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "2rem",
            padding: "2.4rem",
            display: "flex",
            flexDirection: "column",
            gap: "1.6rem"
          }}
        >
          <Typography
            sx={{
              fontSize: "1.8rem",
              fontWeight: 600,
              color: "text.primary"
            }}
            data-testid="have-questions-card-title"
          >
            {t("haveQuestions")}
          </Typography>
          <Typography
            sx={{
              fontSize: "1.4rem",
              fontWeight: 500,
              color: "text.primary",
              lineHeight: 1.5,
              letterSpacing: "0.04rem"
            }}
            data-testid="have-questions-card-copy"
          >
            {t("fillOutForm")}
          </Typography>
          <ColletteButton
            variant="outlined"
            size="large"
            onClick={onRequestQuote}
            label={t("requestAQuote")}
            testId="request-a-quote-button"
            sx={{
              alignSelf: "start"
            }}
          />
        </Box>

        {/* Offer Card */}
        {offerDetails && (
          <Box
            data-testid="offer-card"
            sx={{
              flex: { legacyTablet: 1, legacyDesktop: "none" },
              backgroundColor: "background.default",
              border: "1px solid",
borderColor: "divider",
              boxShadow: 6,
              borderRadius: "2rem",
              padding: "2.4rem",
              display: "flex",
              flexDirection: "column",
              gap: "1.6rem"
            }}
          >
            {/* Offer Badge */}

            <Box
              sx={{
                backgroundColor: "accent.gold",
                borderRadius: "1rem",
                padding: "0.6rem 1rem",
                width: "fit-content"
              }}
            >
              <Typography
                sx={{
                  fontSize: "1.2rem",
                  fontWeight: 600,
                  color: "text.primary"
                }}
                data-testid="offer-details-card-badge"
              >
                {offerDetails.offerTitle}*
              </Typography>
            </Box>

            {/* Offer Description (truncated to 3 lines) */}
            <Typography
              sx={{
                fontSize: "1.4rem",
                fontWeight: 500,
                color: "text.primary",
                lineHeight: 1.5,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical"
              }}
              data-testid="offer-details-card-copy"
            >
              {offerDetails.offerDescription}
            </Typography>

            {/* View Details Link */}
            {!offerDetails?.isHotDeal && <Typography
              component="span"
              onClick={onViewOfferDetails}
              data-testid="view-offer-details-link"
              sx={{
                fontSize: "1.4rem",
                fontWeight: 700,
                color: "text.primary",
                textDecoration: "underline",
                cursor: "pointer",
                width: "fit-content"
              }}
            >
              {t("viewDetails")}
            </Typography>}
          </Box>
        )}
      </Box>

      {/* Special Event & Reverse Itinerary Callout - Only shows when selected date has Special Event and Reverse Itinerary */}
      {(specialEventData && showSpecialEvent) && hasReverseItinerary && (
        <Box
          data-testid="special-event-reverse-itinerary-callout"
          sx={{
            backgroundColor: "accent.goldLight",
            borderRadius: "2rem",
            padding: "2.4rem",
            display: "flex",
            gap: "1.2rem",
            alignItems: "flex-start"
          }}
        >
          {/* Icon placeholder - will be replaced with actual icon */}
          <Box
              component="img"
              src="/images/inclusions/special-event.svg"
              alt="Special Event Icon"
              sx={{
                width: "2.4rem",
                height: "2.4rem",
                flexShrink: 0
              }}
              data-testid="special-event-icon"
          >
            {/* Special event icon */}
          </Box>
          <Stack spacing={0.8}>
            <Typography
              sx={{
                fontSize: "1.8rem",
                fontWeight: 600,
                color: "text.primary"
              }}
              data-testid="special-event-title"
            >
              {specialEventData.title}
            </Typography>
            <Typography
              sx={{
                fontSize: "1.6rem",
                fontWeight: 500,
                color: "text.primary"
              }}
              data-testid="special-event-copy"
            >
              {specialEventData.description}
            </Typography>
            

          </Stack>

          <Box
                component="img"
                src="/images/inclusions/reverse-itinerary.svg"
                alt="Reverse Itinerary Icon"
                sx={{
                  width: "2.4rem",
                  height: "2.4rem",
                  flexShrink: 0
              }}
              data-testid="reverse-itinerary-icon"
          >
            {/* Reverse itinerary icon */}
          </Box>
          <Stack spacing={0.8}>
            <Typography
              sx={{
                fontSize: "1.8rem",
                fontWeight: 600,
                color: "text.primary"
              }}
              data-testid="reverse-itinerary-title"
            >
              {t("Reverse Itinerary Title")}
            </Typography>
            <Typography
              sx={{
                fontSize: "1.6rem",
                fontWeight: 500,
                color: "text.primary"
              }}
              data-testid="reverse-itinerary-title"
            >
              {t("Reverse Itinerary Description")}
            </Typography>
            
          </Stack>

        </Box>
      )}

       {/* Special Event Only Callout - Only shows when selected date has Special Event */}
      {(specialEventData && showSpecialEvent) && !hasReverseItinerary && (
        <Box
          data-testid="special-event-callout"
          sx={{
            backgroundColor: "accent.goldLight",
            borderRadius: "2rem",
            padding: "2.4rem",
            display: "flex",
            gap: "1.2rem",
            alignItems: "flex-start"
          }}
        >
          {/* Icon placeholder - will be replaced with actual icon */}
          <Box
            component="img"
              src="/images/inclusions/special-event.svg"
              alt="Special Event Icon"
              sx={{
                width: "3.2rem",
                height: "2rem",
                flexShrink: 0,
                marginTop: "0.5rem"
              }}
              data-testid="special-event-icon"
          >
            {/* Special event icon */}
          </Box>
          <Stack spacing={0.8}>
            <Typography
              sx={{
                fontSize: "1.8rem",
                fontWeight: 600,
                color: "text.primary"
              }}
            >
              {specialEventData.title}
            </Typography>
            <Typography
              sx={{
                fontSize: "1.6rem",
                fontWeight: 500,
                color: "text.primary"
              }}
            >
              {specialEventData.description}
            </Typography>
            
          </Stack>

        </Box>
      )}

       {/* Reverse Itinerary Only Callout - Only shows when selected date has Reverse Itinerary */}
      {hasReverseItinerary && !showSpecialEvent && (
        <Box
          data-testid="reverse-itinerary-callout"
          sx={{
            backgroundColor: "accent.goldLight",
            borderRadius: "2rem",
            padding: "2.4rem",
            display: "flex",
            gap: "1.2rem",
            alignItems: "flex-start"
          }}
        >
          {/* Icon placeholder - will be replaced with actual icon */}
          <Box
              component="img"
              src="/images/inclusions/reverse-itinerary.svg"
              alt="Reverse Itinerary Icon"
              sx={{
                width: "2.4rem",
                height: "2.4rem",
                flexShrink: 0
             }}
          >
            {/* Reverse Itinerary icon */}
          </Box>
          <Stack spacing={0.8}>
            <Typography
              sx={{
                fontSize: "1.8rem",
                fontWeight: 600,
                color: "text.primary"
              }}
              component="h6"
            >
               {t("Reverse Itinerary Title")}
            </Typography>
            <Typography
              sx={{
                fontSize: "1.6rem",
                fontWeight: 500,
                color: "text.primary"
              }}
            >
              {t("Reverse Itinerary Description")}
            </Typography>
            

          </Stack>

        </Box>
      )}



      {/* Inclusions List */}
      <Stack spacing={2.4}>
        {cleanedInclusions.map((inclusion, index) => (
          <InclusionItem
            key={inclusion.id}
            index={index}
            inclusion={inclusion}
            onClick={
              inclusion.title === "On-tour air included on this tour"
                ? handleOpenOnTourAir
                : undefined
            }
          />
        ))}
      </Stack>
    </Stack>
  );
};

export default InclusionsSection;
